/**
 * Twinkle Product Form — shared helpers
 * @module product-form-helpers
 */
window.TwinkleProductForm = window.TwinkleProductForm || {};

TwinkleProductForm.smoothScrollTo = function (element, duration = 600) {
  if (!element) return;
  const offset = window.innerHeight * 0.25;
  const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - offset;
  const startPosition = window.pageYOffset;
  const distance = targetPosition - startPosition;
  let startTime = null;

  function ease(t, b, c, d) {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  }

  function animation(currentTime) {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    window.scrollTo(0, ease(timeElapsed, startPosition, distance, duration));
    if (timeElapsed < duration) requestAnimationFrame(animation);
  }

  requestAnimationFrame(animation);
};

TwinkleProductForm.buildPackagePropertiesFromForm = function (formDataMain) {
  const properties = {};

  for (const [key, value] of formDataMain.entries()) {
    if (!key.startsWith('properties')) continue;
    const propertyPath = key.replace(/^properties/, '');
    const keys = propertyPath.split(/\[|\]/).filter(Boolean);
    let current = properties;

    keys.forEach((propertyKey, index) => {
      if (index === keys.length - 1) {
        current[propertyKey] = value;
        return;
      }
      if (!current[propertyKey] || typeof current[propertyKey] !== 'object') {
        current[propertyKey] = {};
      }
      current = current[propertyKey];
    });
  }

  return properties;
};

TwinkleProductForm.packageHasAccessories = function (properties) {
  if (!properties || typeof properties !== 'object') return false;

  for (const key of Object.keys(properties)) {
    if (!key.startsWith('_pdp_addon_')) continue;
    const [, qtyRaw] = String(properties[key]).split(':');
    if ((parseInt(qtyRaw, 10) || 0) > 0) return true;
  }

  const accessories = properties.Accessories;
  if (accessories && typeof accessories === 'object') {
    return Object.values(accessories).some((qty) => (parseInt(qty, 10) || 0) > 0);
  }

  return false;
};

TwinkleProductForm.removePackageAddonLines = async function (cartItems, productHandle, cartElement) {
  const updates = {};
  cartItems.forEach((item) => {
    if (item.properties?._package === productHandle) {
      updates[item.key] = 0;
    }
  });

  if (Object.keys(updates).length === 0) return null;

  const body = { updates };
  if (cartElement) {
    body.sections = cartElement.getSectionsToRender().map((section) => section.id);
    body.sections_url = window.location.pathname;
  }

  const response = await fetch(`${routes.cart_update_url}`, {
    ...fetchConfig(),
    body: JSON.stringify(body),
  });

  return response.json();
};

TwinkleProductForm.syncPdpAddonInputsFromUI = function (formOrProductForm) {
  const root =
    formOrProductForm?.closest?.('customization-product') ||
    formOrProductForm?.querySelector?.('customization-product') ||
    formOrProductForm;
  const form = root?.querySelector?.('form') || formOrProductForm;
  if (!form) return;

  (root || form).querySelectorAll('input.input-number[name^="properties[_pdp_addon_"]').forEach((visible) => {
    const name = visible.name;
    const qty = parseInt(visible.value, 10) || 0;
    const variantId = visible.dataset.variant;
    const hidden = form.querySelector(`input[type="hidden"][name="${name}"]`);
    if (hidden && variantId) {
      hidden.value = `${variantId}:${qty}`;
    }
    const handle = visible.dataset.handle;
    if (handle) {
      const accessoriesInput = form.querySelector(`input[name="properties[Accessories][${handle}]"]`);
      if (accessoriesInput) accessoriesInput.value = qty;
    }
  });
};

TwinkleProductForm.appendPackageAddonsToFormData = function (formData, formDataMain, productHandle) {
  let addonIndex = 1;

  for (const [key, value] of formDataMain.entries()) {
    if (key.startsWith('properties[_pdp_addon_') && value) {
      const [id, qtyRaw] = value.split(':');
      const qty = parseInt(qtyRaw, 10) || 0;

      formData.append(`items[${addonIndex}][id]`, id);
      formData.append(`items[${addonIndex}][quantity]`, qty);
      formData.append(`items[${addonIndex}][properties][_addonProduct]`, true);
      formData.append(`items[${addonIndex}][properties][_package]`, productHandle);
      addonIndex++;
    }
  }

  return formData;
};

/**
 * Rebuild FormData so multi-file property inputs become numbered Shopify properties:
 * properties[photo], properties[photo 2], properties[photo 3], ...
 */
TwinkleProductForm.expandMultiFileProperties = function (formData, form) {
  if (!formData || !form) return formData;

  const fileInputs = Array.from(form.querySelectorAll('input.additional_input_field[type="file"]'));
  if (!fileInputs.length) return formData;

  const fileInputNames = new Set(fileInputs.map((input) => input.name));
  const expanded = new FormData();

  for (const [key, value] of formData.entries()) {
    if (fileInputNames.has(key)) continue;
    expanded.append(key, value);
  }

  fileInputs.forEach((input) => {
    const maxFiles = Math.max(1, parseInt(input.dataset.fileLimit, 10) || 1);
    const files = Array.from(input.files || []).slice(0, maxFiles);
    if (!files.length) return;

    const match = input.name.match(/^properties\[(.+)\]$/);
    if (!match) {
      files.forEach((file) => expanded.append(input.name, file));
      return;
    }

    const baseName = match[1];
    files.forEach((file, index) => {
      const propName = index === 0 ? baseName : `${baseName} ${index + 1}`;
      expanded.append(`properties[${propName}]`, file);
    });
  });

  return expanded;
};
